import { LoginResponse } from '@inplay/models';
import { isUndefined } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import CloseEye from '../../assets/close_eye.png';
import ShowEye from '../../assets/show_eye.png';
import {
  useChangeProfilePassword,
  useLoginData,
} from '../../hooks/useAuthData';
import { useFetchBranchEmail } from '../../hooks/useInitialBranchEmail';
import { AuthService } from '../../service/authService';
import { ForgotPasswordResponse } from '../../service/service.type';
import { apiStatusCode } from '../../utils/helper.type';
import Input from '../Input/input';
import Button from '../button/button';
import GeneralModal from '../modal/GeneralModal';
import VenueBranchDropdown from '../venueBranchDropdown/venueBranchDropdown';
import ResetUniversalStaff from './ResetUniversalStaff';
import {
  EditUniversalStaffLoginModalFormData,
  EditUniversalStaffLoginModalProps,
  PasswordChangeResponse,
} from './type';

const EditUniversalStaffLoginModal: React.FC<
  EditUniversalStaffLoginModalProps
> = (props) => {
  const { t } = useTranslation();
  const {
    selectedBranch: hookSelectedBranch,
    email,
    isLoading: isBranchLoading,
    setSelectedBranch,
    refetch,
  } = useFetchBranchEmail();

  const [formData, setFormData] =
    useState<EditUniversalStaffLoginModalFormData>({
      email: email,
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      venueBranchId: '',
    });
  const [showCurrentPassword, setShowCurrentPassword] =
    useState<boolean>(false);
  const [showNewPassword, setShowNewPassword] = useState<boolean>(false);
  const [showConfirmPassword, setShowConfirmPassword] =
    useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [sendingResetLink, setSendingResetLink] = useState<boolean>(false);
  const [showResetPasswordModal, setShowResetPasswordModal] =
    useState<boolean>(false);
  const [resetLinkSent, setResetLinkSent] = useState<boolean>(false);
  const [errors, setErrors] = useState<EditUniversalStaffLoginModalFormData>({
    email: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    venueBranchId: '',
  });

  const { changeProfilePassword } = useChangeProfilePassword();
  const { login } = useLoginData();

  useEffect(() => {
    // Initialize with props.selectedBranch if provided
    if (props.selectedBranch) {
      setSelectedBranch(props.selectedBranch);
      setFormData((prev) => ({ ...prev, email: email || '' }));
    }
  }, [props.selectedBranch, hookSelectedBranch, email]);

  const handleFormDataChange: (field: string, value: string) => void = (
    field: string,
    value: string,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: '' }));
  };

  const handleCloseResetModal = () => {
    setShowResetPasswordModal(false);
    props.onClose();
    props.setShowChangePasswordSuccessToast &&
      props.setShowChangePasswordSuccessToast(true);
    refetch();
    setFormData((prev) => ({ ...prev, email: email || '' }));
  };

  const handleSave = (): void => {
    const newErrors = { ...errors };
    let hasErrors = false;

    if (!formData.email) {
      newErrors.email = t('universalStaffLoginModal.required');
      hasErrors = true;
    }
    if (!formData.currentPassword) {
      newErrors.currentPassword = t(
        'universalStaffLoginModal.currentPasswordRequired',
      );
      hasErrors = true;
    }
    if (!formData.newPassword) {
      newErrors.newPassword = t('universalStaffLoginModal.newPasswordRequired');
      hasErrors = true;
    }
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t(
        'universalStaffLoginModal.confirmPasswordRequired',
      );
      hasErrors = true;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t(
        'universalStaffLoginModal.passwordsDoNotMatch',
      );
      hasErrors = true;
    }

    setErrors(newErrors);
    if (!hasErrors) {
      onSave(formData.currentPassword, formData.newPassword);
    }
  };

  const getUniversalStaffIdpToken = async (
    email: string,
    password: string,
  ): Promise<string | null> => {
    try {
      const verifyResponse: LoginResponse | undefined = await login(
        email,
        password,
      );
      // Type guard to check if response has idpToken (non-MFA case)
      if (verifyResponse && 'idpToken' in verifyResponse) {
        return verifyResponse.idpToken || null;
      }
      return null;
    } catch (error) {
      console.error('Error getting universal staff IDP token:', error);
      return null;
    }
  };

  const onSave: (
    currentPassword: string,
    newPassword: string,
  ) => Promise<void> = async (
    currentPassword: string,
    newPassword: string,
  ): Promise<void> => {
    try {
      setIsLoading(true);

      // First get the universal staff user's IDP token
      const universalStaffIdpToken: string | null =
        await getUniversalStaffIdpToken(formData.email, currentPassword);

      if (!universalStaffIdpToken) {
        toast.error(t('universalStaffLoginModal.invalidCurrentPassword'));
        return;
      }

      const changeProfilePasswordResponse: ForgotPasswordResponse | undefined =
        await changeProfilePassword(
          currentPassword,
          newPassword,
          universalStaffIdpToken,
          false, // Don't navigate to profile page
          formData.email, // Pass the universal staff user's email
        );

      if (!isUndefined(changeProfilePasswordResponse)) {
        const updateUserSuccessResponse: PasswordChangeResponse | null =
          changeProfilePasswordResponse && {
            statusCode: apiStatusCode.SUCCESS,
            data: changeProfilePasswordResponse,
          };

        console.log(`[UpdateUser] response `, updateUserSuccessResponse);
        if (updateUserSuccessResponse?.statusCode === apiStatusCode.SUCCESS) {
          console.log(`[UpdateUser] close modal `);
          props.onClose();
          props.setShowChangePasswordSuccessToast &&
            props.setShowChangePasswordSuccessToast(true);
        }
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      toast.error(t('universalStaffLoginModal.changePasswordError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    try {
      if (!formData.email) {
        toast.error(t('universalStaffLoginModal.emailRequired'));
        return;
      }
      setSendingResetLink(true);
      const authService = new AuthService();
      await authService.forgotPassword(formData.email);
      setResetLinkSent(true);
      toast.success(t('universalStaffLoginModal.resetLinkSent'));
    } catch (error) {
      console.error('Error sending reset password link:', error);
      toast.error(t('universalStaffLoginModal.resetLinkError'));
    } finally {
      setSendingResetLink(false);
    }
  };

  return (
    <>
      <GeneralModal
        onClose={props.onClose}
        headerTitle={t('universalStaffLoginModal.editTitle')}
        headerTitleStyle="text-start font-inter leading-5"
        headerStyle="mt-4"
        body={
          <div className="flex flex-col w-full">
            <div className="text-black mb-6 max-w-2xl text-xs font-normal">
              {' '}
              {t('universalStaffLoginModal.editDescription')}
            </div>
            <div className="space-y-4 mb-4">
              <div className="flex w-full mt-2">
                <label className="w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left">
                  {t('universalStaffLoginModal.venueBranch')}
                </label>
                <div className="w-full">
                  <VenueBranchDropdown
                    selectedBranch={props.selectedBranch}
                    onSelect={(branch) => {
                      if (props.setSelectedBranch) {
                        props.setSelectedBranch(branch);
                        setSelectedBranch(branch);
                      }
                    }}
                    disabled={isLoading || isBranchLoading}
                  />
                </div>
              </div>
              <div className="flex w-full">
                <label className="w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left">
                  {t('universalStaffLoginModal.email')} *
                </label>
                <div className="flex w-full gap-2">
                  <Input
                    value={isBranchLoading ? '' : formData.email}
                    onChange={(e) =>
                      handleFormDataChange('email', e.target.value)
                    }
                    placeholder={
                      isBranchLoading &&
                      props.selectedBranch.label !== 'All Users'
                        ? `...Fetching email for ${props.selectedBranch.label}`
                        : 'example@email.com'
                    }
                    required
                    useDefaultStyling={false}
                    inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
                    containerClassName="flex-1"
                    labelClassName="hidden"
                    disabled={true}
                  />
                  <Button
                    className="px-8 py-3 rounded-full font-normal text-xs bg-white text-[#000000] border border-[#D8D8D8] hover:bg-white hover:text-[#000000]  min-w-[6.5rem]"
                    onClick={() => setShowResetPasswordModal(true)}
                    disabled={isBranchLoading}
                  >
                    {isLoading
                      ? t('common.loading')
                      : t('universalStaffLoginModal.edit')}
                  </Button>
                </div>
              </div>
              <div className="flex w-full relative">
                <div className="flex w-full relative">
                  <label className="w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left">
                    {t('universalStaffLoginModal.currentPassword')} *
                  </label>
                  <div className="flex flex-col w-full">
                    <div className="relative w-full">
                      <Input
                        type={showCurrentPassword ? 'text' : 'password'}
                        value={formData.currentPassword}
                        onChange={(e) =>
                          handleFormDataChange(
                            'currentPassword',
                            e.target.value,
                          )
                        }
                        placeholder={t(
                          'universalStaffLoginModal.enterPassword',
                        )}
                        inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
                        containerClassName="flex w-full"
                      />
                      <div
                        className="passowrd-input-container absolute right-4 top-[14px] cursor-pointer"
                        onClick={() =>
                          setShowCurrentPassword?.(!showCurrentPassword)
                        }
                      >
                        {showCurrentPassword ? (
                          <img
                            src={ShowEye}
                            alt="Show Password"
                            className="w-5 h-4"
                          />
                        ) : (
                          <img
                            src={CloseEye}
                            alt="Hide Password"
                            className="w-5 h-3"
                          />
                        )}
                      </div>
                    </div>
                    {errors.currentPassword && (
                      <div className="text-xs text-red-500 ms-5">
                        {errors.currentPassword}
                      </div>
                    )}
                    <div className="flex justify-end mt-3">
                      {sendingResetLink && !resetLinkSent ? (
                        <div className="text-xs bg-white hover:bg-white hover:text-THEME_COLOR text-THEME_COLOR font-normal">
                          {t(
                            'universalStaffLoginModal.sendingPasswordResetLink',
                          )}
                        </div>
                      ) : resetLinkSent ? (
                        <div className="text-xs bg-white hover:bg-white hover:text-THEME_COLOR text-THEME_COLOR font-normal">
                          {t('universalStaffLoginModal.resetLinkSent')}{' '}
                          {`${formData?.email}`}
                        </div>
                      ) : (
                        <Button
                          onClick={handleForgotPassword}
                          className="text-xs bg-white hover:bg-white hover:text-[#000000] text-[#000000] underline font-normal"
                          disabled={isLoading}
                        >
                          {t('universalStaffLoginModal.forgotPassword')}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="flex w-full relative">
                <label className="w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left">
                  {t('universalStaffLoginModal.newPassword')} *
                </label>
                <div className="flex flex-col w-full">
                  <div className="relative w-full">
                    <Input
                      type={showNewPassword ? 'text' : 'password'}
                      value={formData.newPassword}
                      onChange={(e) =>
                        handleFormDataChange('newPassword', e.target.value)
                      }
                      placeholder={t(
                        'universalStaffLoginModal.enterNewPassword',
                      )}
                      useDefaultStyling={false}
                      inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
                      containerClassName="flex w-full"
                    />
                    <div
                      className="passowrd-input-container absolute right-4 top-[52px] cursor-pointer"
                      onClick={() => setShowNewPassword?.(!showNewPassword)}
                    >
                      {showNewPassword ? (
                        <img
                          src={ShowEye}
                          alt="Show Password"
                          className="w-5 h-4"
                        />
                      ) : (
                        <img
                          src={CloseEye}
                          alt="Hide Password"
                          className="w-5 h-3"
                        />
                      )}
                    </div>
                  </div>
                  {errors.newPassword && (
                    <div className="text-xs text-red-500 ms-5">
                      {errors.newPassword}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex w-full relative">
                <label className="w-[40%] pt-4 text-black font-helvetica text-xs font-[500] text-left">
                  {t('universalStaffLoginModal.confirmPassword')} *
                </label>
                <div className="flex flex-col w-full">
                  <div className="relative w-full">
                    <Input
                      type={showConfirmPassword ? 'text' : 'password'}
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        handleFormDataChange('confirmPassword', e.target.value)
                      }
                      placeholder={t(
                        'universalStaffLoginModal.enterConfirmPassword',
                      )}
                      useDefaultStyling={false}
                      inputClassName="rounded-full bg-[#FAFAFA] px-6 py-3 w-full"
                      containerClassName="flex w-full"
                    />
                    <div
                      className="passowrd-input-container absolute right-4 top-[52px] cursor-pointer"
                      onClick={() =>
                        setShowConfirmPassword?.(!showConfirmPassword)
                      }
                    >
                      {showConfirmPassword ? (
                        <img
                          src={ShowEye}
                          alt="Show Password"
                          className="w-5 h-4"
                        />
                      ) : (
                        <img
                          src={CloseEye}
                          alt="Hide Password"
                          className="w-5 h-3"
                        />
                      )}
                    </div>
                  </div>
                  {errors.confirmPassword && (
                    <div className="text-xs text-red-500 ms-5">
                      {errors.confirmPassword}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        }
        footer={
          <>
            <Button
              className="px-8 py-3 rounded-full font-normal text-xs bg-white text-[#A7A7A7] border border-[#D8D8D8] hover:bg-white hover:text-[#A7A7A7]  min-w-[6.5rem]"
              onClick={props.onClose}
            >
              {t('universalStaffLoginModal.cancel')}
            </Button>
            <Button
              className="px-8 py-3 rounded-full font-normal text-xs min-w-[6.5rem]"
              onClick={handleSave}
              disabled={isLoading || isBranchLoading}
            >
              {isLoading || isBranchLoading
                ? t('common.loading')
                : t('universalStaffLoginModal.save')}
            </Button>
          </>
        }
        footerStyle="justify-between flex px-0"
      />
      {showResetPasswordModal && props.selectedBranch && (
        <ResetUniversalStaff
          email={formData.email}
          onClose={handleCloseResetModal}
          selectedVenueBranch={props.selectedBranch}
          setSelectedVenueBranch={props.setSelectedBranch}
        />
      )}
    </>
  );
};

export default EditUniversalStaffLoginModal;
